import './styles.css'
import { initializePlayer, setGlobalLoggerInPlayer } from './player/player'
import { BatchCollectionManager } from './monitoring/comprehensiveDataStructure'
import { setGlobalLoggerInApi } from './data/api'
import { setGlobalLoggerInEvent } from './ui/event'
import { setGlobalLoggerInHandlers } from './ui/handler'
import { setGlobalLoggerInVastTracking } from './utils/vast-tracking'
import { setGlobalLoggerInVideoTracking } from './utils/video-tracking'

// Global comprehensive logger instance
let globalLogger = null
let flushIntervalId = null

// Service Worker registration
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js')
      console.log('Service Worker registered:', registration.scope)
      return registration
    } catch (error) {
      console.error('Service Worker registration failed:', error)
      throw error
    }
  } else {
    console.warn('Service Worker not supported')
  }
}

const initializeLogger = async () => {
  const screenUuid =
    new URLSearchParams(window.location.search).get('screenUuid') || 'main-app-default'

  try {
    // Use consistent sessionId based on screenUuid + date, not timestamp
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    const sessionId = `${screenUuid}-${today}`

    // Check for debug mode via URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    const debugMode = urlParams.get('debug') === 'true'

    globalLogger = new BatchCollectionManager({
      sessionId: sessionId,
      screenUuid: screenUuid,
      batchInterval: 30000,
      batchSize: 50,
      debug: debugMode
    })
    await globalLogger.initialize(sessionId)
    // await globalLogger.flushBatch()

    // Debug: Expose globalLogger to window for testing
    window.globalLogger = globalLogger

    // Connect loggers to all handlers
    setGlobalLoggerInApi(globalLogger)
    setGlobalLoggerInEvent(globalLogger)
    setGlobalLoggerInHandlers(globalLogger)
    setGlobalLoggerInPlayer(globalLogger)
    setGlobalLoggerInVastTracking(globalLogger)
    setGlobalLoggerInVideoTracking(globalLogger)

    // Log app initialization
    await globalLogger.addEvent('app_initialization_start', {
      entry_point: 'index.js',
      screen_uuid: screenUuid,
      user_agent: navigator.userAgent,
      page_url: window.location.href
    })
  } catch (error) {
    console.warn('Failed to initialize comprehensive logger:', error)
  }
}

const initializeApp = async () => {
  // Initialize comprehensive logger FIRST
  await initializeLogger()

  // Register Service Worker
  try {
    const swStart = performance.now()
    await registerServiceWorker()
    const swTime = performance.now() - swStart

    // Log service worker registration
    if (globalLogger) {
      await globalLogger.addEvent('service_worker_registered', {
        registration_time_ms: swTime,
        success: true
      })
    }
  } catch (error) {
    console.warn('Service Worker registration failed, continuing without it')

    // Log service worker failure
    if (globalLogger) {
      await globalLogger.addEvent('service_worker_registration_failed', {
        error_message: error.message,
        error_stack: error.stack
      })
    }
  }

  initializePlayer()

  // Log successful app initialization
  if (globalLogger) {
    await globalLogger.addEvent('app_initialization_complete', {
      initialization_time_ms: performance.now(),
      event_listeners_count: 3, // video, image, vast
      services_initialized: ['batch_collection', 'service_worker', 'comprehensive_logger']
    })
  }

  startFlushBatchInterval()
}

const startFlushBatchInterval = async () => {
  await globalLogger.flushBatch()
  flushIntervalId = setInterval(async () => {
    if (globalLogger) {
      await globalLogger.flushBatch()
    }
  }, 60000)
}

window.addEventListener('beforeunload', () => {
  clearInterval(flushIntervalId)
})

document.addEventListener('DOMContentLoaded', initializeApp)
